import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { formatTimeAgo } from '../utils/formatters';

const LOCATION_ORDER = ['Storage', 'Fulfillment', 'Casing'];

export default function InventoryCheckin() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [checkin, setCheckin] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Local per-item edit state, keyed by item.id.
  const [draft, setDraft] = useState({});       // { [itemId]: { counted_qty, needs_reorder, note } }
  const [savingId, setSavingId] = useState(null);
  const [completing, setCompleting] = useState(false);

  const fetchCheckin = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/inventory/checkins/${id}`);
      setCheckin(data.checkin);
      setItems(data.items);
      // Seed the draft from any existing reviews so re-visits restore in-progress edits.
      const seed = {};
      for (const it of data.items) {
        if (it.reviewed_at) {
          seed[it.id] = {
            counted_qty: it.counted_qty,
            needs_reorder: !!it.review_needs_reorder,
            note: it.review_note || '',
          };
        }
      }
      setDraft(seed);
    } catch (err) { console.error('Check-in fetch error:', err); }
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchCheckin(); }, [fetchCheckin]);

  function setDraftField(itemId, field, value) {
    setDraft(prev => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), [field]: value } }));
  }

  async function saveItem(item) {
    const d = draft[item.id] || {};
    const counted = d.counted_qty != null && d.counted_qty !== '' ? parseInt(d.counted_qty) : item.current_stock;
    if (isNaN(counted) || counted < 0) {
      alert('Enter a non-negative count.');
      return;
    }
    setSavingId(item.id);
    try {
      await api.post(`/api/inventory/checkins/${id}/items/${item.id}`, {
        counted_qty: counted,
        needs_reorder: !!d.needs_reorder,
        note: d.note || null,
      });
      await fetchCheckin();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save');
    }
    setSavingId(null);
  }

  async function completeCheckin() {
    if (!window.confirm('Mark this check-in as complete?')) return;
    setCompleting(true);
    try {
      await api.put(`/api/inventory/checkins/${id}`, { completed: true });
      navigate('/inventory');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to complete check-in');
    }
    setCompleting(false);
  }

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>;
  if (!checkin) return <div style={{ padding: 24 }}>Check-in not found. <Link to="/inventory">Back to Inventory</Link></div>;

  // Group items by location with Storage/Fulfillment/Casing first.
  const byLocation = new Map();
  for (const it of items) {
    const key = it.location || '(No location)';
    if (!byLocation.has(key)) byLocation.set(key, []);
    byLocation.get(key).push(it);
  }
  const sortedLocations = [...byLocation.keys()].sort((a, b) => {
    const ai = LOCATION_ORDER.indexOf(a), bi = LOCATION_ORDER.indexOf(b);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.localeCompare(b);
  });

  const reviewedCount = items.filter(it => it.reviewed_at).length;
  const reorderCount = items.filter(it => it.review_needs_reorder).length;

  return (
    <div>
      <div className="page-header" style={{ alignItems: 'center' }}>
        <div>
          <h2 className="page-title" style={{ margin: 0 }}>Inventory Check-in #{checkin.id}</h2>
          <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 4 }}>
            Started {formatTimeAgo(checkin.started_at)}{checkin.started_by_name ? ` by ${checkin.started_by_name}` : ''}
            {checkin.completed_at ? <span style={{ marginLeft: 8, color: 'var(--success)', fontWeight: 600 }}>· Completed</span> : null}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to="/inventory" className="btn btn-secondary">Back</Link>
          {!checkin.completed_at && (
            <button className="btn btn-primary" onClick={completeCheckin} disabled={completing}>
              {completing ? 'Completing…' : 'Complete Check-in'}
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: '12px 16px', background: 'var(--bg-card)', borderRadius: 8, boxShadow: 'var(--shadow)', marginBottom: 16, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13 }}>
          <strong>{reviewedCount}</strong> / {items.length} items reviewed
          <div style={{ background: 'var(--bg)', height: 6, borderRadius: 3, marginTop: 4, width: 200, overflow: 'hidden' }}>
            <div style={{ background: 'var(--success)', height: '100%', width: `${items.length ? (reviewedCount / items.length) * 100 : 0}%`, transition: 'width 0.2s' }} />
          </div>
        </div>
        <div style={{ fontSize: 13, color: reorderCount > 0 ? '#9b2c2c' : 'var(--text-light)' }}>
          <strong>{reorderCount}</strong> flagged for reorder this session
        </div>
      </div>

      {sortedLocations.map(loc => (
        <div key={loc} style={{ marginBottom: 24 }}>
          <h3 style={{ position: 'sticky', top: 0, background: 'var(--bg)', padding: '8px 0', margin: 0, zIndex: 5, borderBottom: '1px solid var(--border)' }}>
            {loc} <span style={{ fontSize: 12, color: 'var(--text-light)', fontWeight: 400 }}>({byLocation.get(loc).length} items)</span>
          </h3>
          <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
            {byLocation.get(loc).map(item => {
              const reviewed = !!item.reviewed_at;
              const d = draft[item.id] || {};
              return (
                <div key={item.id} className="card" style={{ padding: 14, borderLeft: `4px solid ${reviewed ? '#22c55e' : 'var(--border)'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 200 }}>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>{item.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 2 }}>
                        {item.category || 'Uncategorized'} · Previous: <strong>{item.current_stock}</strong> {item.unit}
                        {item.min_stock > 0 ? ` · Min ${item.min_stock}` : ''}
                      </div>
                      {reviewed && (
                        <div style={{ fontSize: 11, color: 'var(--success)', marginTop: 4 }}>
                          ✓ Reviewed {formatTimeAgo(item.reviewed_at)}{item.reviewed_by_name ? ` by ${item.reviewed_by_name}` : ''}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', flex: 1, justifyContent: 'flex-end' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: 'var(--text-light)' }}>New count</label>
                        <input
                          type="number"
                          min={0}
                          value={d.counted_qty ?? ''}
                          placeholder={String(item.current_stock)}
                          onChange={e => setDraftField(item.id, 'counted_qty', e.target.value)}
                          style={{ width: 90, fontSize: 18, padding: '8px 10px' }}
                        />
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', paddingBottom: 8 }}>
                        <input
                          type="checkbox"
                          checked={!!d.needs_reorder}
                          onChange={e => setDraftField(item.id, 'needs_reorder', e.target.checked)}
                          style={{ width: 'auto' }}
                        />
                        Needs reorder
                      </label>
                      <input
                        type="text"
                        value={d.note || ''}
                        placeholder="Note (optional)"
                        onChange={e => setDraftField(item.id, 'note', e.target.value)}
                        style={{ width: 200 }}
                      />
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => saveItem(item)}
                        disabled={savingId === item.id || !!checkin.completed_at}
                        style={{ height: 36 }}
                      >
                        {savingId === item.id ? 'Saving…' : reviewed ? 'Update' : 'Save'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {items.length === 0 && (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--text-light)' }}>
          No active inventory items to review.
        </div>
      )}
    </div>
  );
}
